/**
 * A tab that loaded the old build asks for a chunk the new deploy has already removed: the request
 * 404s, the page cannot render, and the error boundary used to leave the reader on "processing..."
 * until they reloaded by hand. One reload fetches the new index.html and its chunks, which is the
 * whole fix.
 *
 * This reloads by itself, unlike the new-version notice, because there is nothing left to lose: the
 * page the reader asked for never rendered. It reloads once - a second chunk failure within the
 * window means the deploy itself is broken, and a loop would hide that behind a flickering tab.
 */
const RELOADED_AT_KEY = 'orybook.chunkReloadAt';
const RETRY_WINDOW_MS = 60_000;

export const isChunkLoadError = (error: unknown): boolean => {
    if (!error || typeof error !== 'object') return false;
    const { name, message } = error as { name?: unknown; message?: unknown };
    return name === 'ChunkLoadError' || (typeof message === 'string' && /Loading (CSS )?chunk [\w-]+ failed/i.test(message));
};

interface IChunkReloadDeps {
    now: number;
    storage: Pick<Storage, 'getItem' | 'setItem'> | null;
    reload: () => void;
}

/** Reloads the page for a chunk that is gone, at most once a minute. Returns whether it reloaded. */
export const reloadOnceForChunkError = (error: unknown, deps: IChunkReloadDeps): boolean => {
    if (!isChunkLoadError(error)) return false;
    try {
        const last = Number(deps.storage?.getItem(RELOADED_AT_KEY) ?? 0);
        if (last && deps.now - last < RETRY_WINDOW_MS) return false;
        deps.storage?.setItem(RELOADED_AT_KEY, String(deps.now));
    } catch {
        // No storage (private window, blocked site data): without a way to remember the attempt,
        // reloading could loop, so leave the page as it is.
        return false;
    }
    deps.reload();
    return true;
};

const sessionStore = (): Storage | null => {
    try {
        return window.sessionStorage;
    } catch {
        return null;
    }
};

export const reloadOnceForChunkErrorInBrowser = (error: unknown): boolean =>
    reloadOnceForChunkError(error, { now: Date.now(), storage: sessionStore(), reload: () => window.location.reload() });
